/* eslint-disable max-lines-per-function */
import { useEffect, useState } from 'react';
import { useAddServiceToCart } from '@catalogo/service-americanas-cart/src/hooks/add-service-to-cart';
import { useTestAB } from '@catalogo/core-experiments';
import { fmtLinesToAddInCart } from '../helpers/add-to-cart';
import {
  addToCartEffect,
  setActionsEffect,
  handleAddToCart,
  getBasketUrl,
  getServicesToAdd,
  onServiceIsAdded,
  getNextUrl,
} from '../helpers/add-to-cart-pdp';
import { IUseAddToCartPDPProps, IActionsState } from '../types/add-to-cart-pdp';
/**
 * Hook que disponibiliza a função que inicia o fluxo de adicionar o(s) produto(s) no cart e gerencia o fluxo até a cesta ou garantia na PDP.
 * @return {void}
 */
export const useAddToCartPDP = ({
  showModal,
  history,
  url,
  brand,
  addToCart,
  cartId,
  cartLines,
  isAddedToCart,
  isUpdatedToCart,
  addToCartError,
  offers,
  hasServices,
  servicePDPinfoForWarranty,
  currentSellerStoreId,
  buyTogetherVIP,
  currentSku,
  isMobile,
  modalWarrantyExperiment,
}: IUseAddToCartPDPProps) => {
  const cartExperiment: string = useTestAB('EXP_NS_07_NEW_CART_URL') || 'OLD';
  const [isServiceAdded, setIsServiceAdded] = useState<boolean>(false);
  const [actions, setActions] = useState<IActionsState>({ add: false, update: false, addService: false });
  const { offersToAdd, offersToUpdate } = fmtLinesToAddInCart({
    offers,
    cartLines,
    sellerStoreId: currentSellerStoreId,
    servicePDPinfoForWarranty,
  });
  const hasStateFromMutation = isAddedToCart || isUpdatedToCart || isServiceAdded;
  const basketUrl = getBasketUrl({
    brand: brand as string,
    cartId: cartId as string,
    sellerStoreId: currentSellerStoreId,
    experiment: cartExperiment,
  });
  const hasServiceToAddWithProduct = offersToAdd?.some(offer => offer?.services?.length > 0);
  const isAcom = brand?.toLowerCase() === 'americanas' || brand?.toLowerCase() === 'acom';

  const nextUrl = getNextUrl({
    isAcom,
    hasServices,
    hasServiceToAddWithProduct,
    showModal: showModal as boolean,
    basketUrl,
    url,
    isMobile: isMobile as boolean,
    modalWarrantyExperiment,
    cartExperiment,
  });

  const servicestoAdd = getServicesToAdd(hasServiceToAddWithProduct, servicePDPinfoForWarranty);

  const { addServiceToCartFn, errorAddService } = useAddServiceToCart({
    onMutationComplete: onServiceIsAdded(setIsServiceAdded),
  });

  useEffect(
    addToCartEffect({
      showModal,
      history,
      nextUrl: nextUrl as string,
      cartId,
      cartLines,
      addToCartError,
      errorAddService,
      isAddedToCart: isAddedToCart || (offersToAdd?.length === 0 && hasStateFromMutation),
      isUpdatedToCart: isUpdatedToCart || (offersToUpdate?.length === 0 && hasStateFromMutation),
      isServiceAdded: isServiceAdded || (servicestoAdd?.length === 0 && hasStateFromMutation),
      hasServices,
      servicePDPinfoForWarranty,
      currentSku,
      cartExperiment,
    }),
    [cartId, isAddedToCart, isUpdatedToCart, isServiceAdded, addToCartError, errorAddService]
  );

  useEffect(
    setActionsEffect({
      actions,
      cartId,
      cartLines,
      addToCart,
      addServiceToCartFn,
      offersToAdd,
      offersToUpdate,
      servicestoAdd,
      isAddedToCart,
      isUpdatedToCart,
      isServiceAdded,
    }),
    [actions, isAddedToCart, isUpdatedToCart, isServiceAdded]
  );

  const handleAddProduct = handleAddToCart({
    nextUrl: nextUrl as string,
    offers: { offersToAdd, offersToUpdate },
    servicestoAdd,
    setActions,
    hasServices,
    history,
    servicePDPinfoForWarranty,
    showModal,
    buyTogetherVIP,
    currentSku,
    cartExperiment,
  });

  return { handleAddProduct };
};
